import Checkbox from "@mui/joy/Checkbox";
import Input from "@mui/joy/Input";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Stack from "@mui/joy/Stack";
import React from "react";
import {
  QualitativeTimeDelta,
  QuantitativeTimeDelta,
  RelativeTime,
  TemporalPrecision,
  TimeInterval,
  TimeReference,
  TimeType,
} from "../../../../models/human_time";
import { isApproximateTime, isRelativeTime, isTimeInterval } from "../../../../shared/utils/fact";
import { Temporality } from "../DrawerContent/FactDisplayAndEdit/TemporalityEdit";

interface RelativeDatePickerProps {
  readonly time: RelativeTime | TimeInterval;
  readonly temporality: Temporality;
  readonly onUpdate: (newValue: RelativeTime | TimeInterval) => void;
}

// Helper function to determine if precision should be added
const shouldAddPrecision = (
  time: RelativeTime | TimeInterval,
  temporality: Temporality
): boolean => {
  if (isTimeInterval(time)) {
    const targetTime = temporality === Temporality.PAST ? time.begin : time.end;
    return isRelativeTime(targetTime) && typeof targetTime.delta !== "string";
  }

  if (isRelativeTime(time)) {
    return typeof time.delta !== "string" && time.delta.value !== 0;
  }

  return false;
};

// Helper function to get precision from time object
const getPrecisionFromTime = (
  time: RelativeTime | TimeInterval,
  temporality: Temporality
): TemporalPrecision => {
  if (isTimeInterval(time)) {
    const targetTime = temporality === Temporality.PAST ? time.begin : time.end;
    if (isRelativeTime(targetTime) && typeof targetTime.delta !== "string") {
      return targetTime.delta.precision;
    }
  } else if (isRelativeTime(time)) {
    return typeof time.delta === "string" ? TemporalPrecision.DAY : time.delta.precision;
  }

  return TemporalPrecision.DAY;
};

// Helper function to extract value from time object
const getValueFromTime = (time: RelativeTime | TimeInterval, temporality: Temporality): number => {
  if (isRelativeTime(time)) {
    if (time.delta) {
      return Math.abs((time.delta as QuantitativeTimeDelta).value || 1);
    }
    return 1;
  }

  if (isTimeInterval(time)) {
    if (isApproximateTime(time.begin) || isApproximateTime(time.end)) {
      return 1;
    }

    const targetTime = temporality === Temporality.PAST ? time.begin : time.end;
    return isRelativeTime(targetTime) && typeof targetTime.delta !== "string"
      ? Math.abs(targetTime.delta.value)
      : 1;
  }

  return 1;
};

// Helper function to check if time represents until/from present
const isUntilOrFromPresent = (time: RelativeTime | TimeInterval): boolean => {
  return isTimeInterval(time) && isRelativeTime(time.begin) && isRelativeTime(time.end);
};

// Helper function to create note time reference
const createNoteTimeReference = (): RelativeTime => ({
  type: TimeType.RELATIVE,
  reference: TimeReference.NOTE,
  delta: QualitativeTimeDelta.BEFORE,
});

// Helper function to create relative time delta
const createDelta = (
  newTemporality: Temporality,
  newAddPrecision: boolean,
  newPrecision: TemporalPrecision,
  value: number
) => {
  if (!newAddPrecision) {
    return newTemporality === Temporality.PAST
      ? QualitativeTimeDelta.BEFORE
      : QualitativeTimeDelta.AFTER;
  }

  const deltaValue = value * (newTemporality === Temporality.PAST ? -1 : 1);
  return { value: deltaValue, precision: newPrecision };
};

// Helper function to generate time interval
const generateTimeInterval = (
  newTemporality: Temporality,
  newAddPrecision: boolean,
  delta: any
): TimeInterval => {
  const noteTime = createNoteTimeReference();
  const relativeTime = {
    type: TimeType.RELATIVE,
    reference: TimeReference.NOTE,
    delta: newAddPrecision ? delta : 1,
  } as RelativeTime;

  return {
    type: TimeType.INTERVAL,
    begin: newTemporality === Temporality.PAST ? relativeTime : noteTime,
    end: newTemporality === Temporality.PAST ? noteTime : relativeTime,
  };
};

// Main function to generate relative time
const generateRelativeTime = (
  newTemporality: Temporality,
  newAddPrecision: boolean,
  newPrecision: TemporalPrecision,
  value: number,
  untilOrFromPresent: boolean
): RelativeTime | TimeInterval => {
  const delta = createDelta(newTemporality, newAddPrecision, newPrecision, value);

  if (untilOrFromPresent) {
    return generateTimeInterval(newTemporality, newAddPrecision, delta);
  }

  return {
    type: TimeType.RELATIVE,
    reference: TimeReference.NOTE,
    delta: delta,
  } as RelativeTime;
};

export default function RelativeDatePicker({
  time,
  temporality,
  onUpdate,
}: RelativeDatePickerProps) {
  const addPrecision = shouldAddPrecision(time, temporality);
  const precision = getPrecisionFromTime(time, temporality);
  const initialValue = getValueFromTime(time, temporality);
  const untilOrFromPresent = isUntilOrFromPresent(time);

  const [value, setValue] = React.useState(initialValue);

  const handleAddPrecisionChange = () => {
    const newAddPrecision = !addPrecision;
    onUpdate(
      generateRelativeTime(temporality, newAddPrecision, precision, value, untilOrFromPresent)
    );
  };

  const handleValueChange = (newValue: number) => {
    setValue(newValue);
    if (!isNaN(newValue)) {
      onUpdate(
        generateRelativeTime(temporality, addPrecision, precision, newValue, untilOrFromPresent)
      );
    }
  };

  const handlePrecisionChange = (newPrecision: TemporalPrecision) => {
    onUpdate(
      generateRelativeTime(temporality, addPrecision, newPrecision, value, untilOrFromPresent)
    );
  };

  const handleUntilFromPresentChange = () => {
    const newUntilOrFromPresent = !untilOrFromPresent;
    onUpdate(
      generateRelativeTime(temporality, addPrecision, precision, value, newUntilOrFromPresent)
    );
  };

  return (
    <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
      <Checkbox
        label="Add a delta"
        size="sm"
        variant="outlined"
        checked={addPrecision}
        onChange={handleAddPrecisionChange}
        data-cy="add-delta-button"
      />
      {addPrecision && (
        <Stack direction="row" spacing={1}>
          <Input
            type="number"
            value={value}
            onChange={(e) => handleValueChange(parseInt(e.target.value))}
            slotProps={{
              input: { min: 1 },
            }}
            data-cy="delta-value"
          />
          <Select
            value={precision}
            onChange={(event, newPrecision) =>
              handlePrecisionChange(newPrecision as TemporalPrecision)
            }
            data-cy="delta-precision"
          >
            <Option value={TemporalPrecision.DAY}>Day</Option>
            <Option value={TemporalPrecision.WEEK}>Week</Option>
            <Option value={TemporalPrecision.MONTH}>Month</Option>
            <Option value={TemporalPrecision.YEAR}>Year</Option>
          </Select>
        </Stack>
      )}
      <Checkbox
        label={temporality === Temporality.PAST ? "Until present date" : "From present date"}
        size="sm"
        variant="outlined"
        disabled={!addPrecision}
        checked={untilOrFromPresent}
        onChange={handleUntilFromPresentChange}
        data-cy="until-from-present-button"
      />
    </Stack>
  );
}
